import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import Select from "react-select";
import { MATERIAL_PAGE, FORMULA_PAGE, FORMULA_MAP_PAGE } from "constants/estimatingDatabasePages";
import { useDatabaseWorkspace, useScopedList } from "hooks/useEstimatingDatabase";
import { DatabasePageShell } from "components/EstimatingDatabase/DatabasePageShell";
import { useListPanel } from "components/EstimatingDatabase/useListPanel";
import { useLookup, unitOptions, formulaChoices, findFormula } from "components/EstimatingDatabase/lookups";
import {
  validateFormula,
  parseFormulaConfig,
  serializeFormulaConfig,
  MATERIAL_FORMULA_PARAMETERS,
} from "helpers/estimatingDatabase/jaceFormula";
import { buildMaterialsWorkbook, readMaterialsWorkbook } from "helpers/estimatingDatabase/databaseXlsx";

const TABS = [
  { key: "materials", label: "Materials" },
  { key: "formulas", label: "Formulas" },
  { key: "maps", label: "Formula assignments" },
];

const paramOptions = MATERIAL_FORMULA_PARAMETERS.map((p) => ({ value: p.value, label: p.label }));

// formula_maps editor: which formula a catalog + material pair uses and
// which model parameter feeds each of its variables (the desktop
// MaterialFormula popup, serialised into FormulaConfig).
const FormulaConfigModal = ({ open, initial, formulas, onCancel, onSubmit }) => {
  const [code, setCode] = useState("");
  const [cfg, setCfg] = useState({ FormulaName: "", Formula: "", VariableParamMap: [] });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCode((initial && initial.Code) || "");
    setCfg(parseFormulaConfig(initial && initial.FormulaConfig) || { FormulaName: "", Formula: "", VariableParamMap: [] });
    setError(null);
  }, [open, initial]);

  const check = cfg.Formula ? validateFormula(cfg.Formula) : null;
  const variables = check && check.valid ? check.desktopVariables : [];
  const paramFor = (v) => {
    const hit = (cfg.VariableParamMap || []).find((m) => String(m.Variable).toLowerCase() === v);
    return hit ? hit.Parameter : null;
  };

  const formulaOptions = formulas.map((f, i) => ({ value: `${i}`, label: `${f.Code}: ${f.Formula}`, formula: f }));
  const selectedFormula = findFormula(formulas, cfg.FormulaName, cfg.Formula);
  const selectedOption = selectedFormula
    ? formulaOptions.find((o) => o.formula === selectedFormula)
    : cfg.FormulaName
    ? { value: "stored", label: `${cfg.FormulaName}: ${cfg.Formula}` }
    : null;

  const submit = () => {
    if (!code.trim()) return setError("The catalog + material key is required.");
    if (!cfg.Formula) return setError("Pick a formula.");
    if (!check.valid) return setError(check.error);
    const map = variables.map((v) => ({ Variable: v, Parameter: paramFor(v) }));
    if (map.some((m) => m.Parameter === null || m.Parameter === undefined)) {
      return setError("Map every variable to a parameter; an unmapped formula evaluates to 0.");
    }
    return onSubmit({ ...(initial || {}), Code: code.trim(), FormulaConfig: serializeFormulaConfig({ ...cfg, VariableParamMap: map }) });
  };

  return (
    <Modal open={open} title="Formula assignment" onCancel={onCancel} onOk={submit} okText="Apply" width={640} destroyOnClose>
      <div className="d-flex flex-column gap-3" data-testid="formula-config-modal">
        <div>
          <label className="f-14 mb-1">Catalog division + material name</label>
          <input
            className="form-control"
            value={code}
            disabled={!!(initial && initial._id)}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <div>
          <label className="f-14 mb-1">Formula</label>
          <Select
            options={formulaOptions}
            value={selectedOption}
            onChange={(o) =>
              setCfg((c) => ({ ...c, FormulaName: o ? o.formula.Code : "", Formula: o ? o.formula.Formula : "" }))
            }
          />
          {check && !check.valid && <div className="text-danger f-12 mt-1">{check.error}</div>}
        </div>
        {variables.map((v) => (
          <div key={v} className="d-flex align-items-center gap-2">
            <span className="f-14" style={{ width: 120 }}>
              {v}
            </span>
            <div style={{ flex: 1 }}>
              <Select
                options={paramOptions}
                value={paramOptions.find((o) => o.value === paramFor(v)) || null}
                onChange={(o) =>
                  setCfg((c) => ({
                    ...c,
                    VariableParamMap: [
                      ...(c.VariableParamMap || []).filter((m) => String(m.Variable).toLowerCase() !== v),
                      { Variable: v, Parameter: o ? o.value : null },
                    ],
                  }))
                }
              />
            </div>
          </div>
        ))}
        {error && <div className="text-danger f-12">{error}</div>}
      </div>
    </Modal>
  );
};

export default function MaterialsDatabasePage() {
  const workspace = useDatabaseWorkspace();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("materials");
  const [mapModal, setMapModal] = useState(null);

  const materials = useScopedList(workspace, MATERIAL_PAGE);
  const formulas = useScopedList(workspace, FORMULA_PAGE);
  const maps = useScopedList(workspace, FORMULA_MAP_PAGE);
  const materialClasses = useLookup(workspace, "material_classes");

  const classNames = useMemo(() => materialClasses.map((c) => c.Code).filter(Boolean), [materialClasses]);

  const options = (field) => {
    if (field.options === "units") {
      return unitOptions("units", workspace.template, materials.items.map((it) => it.rec.Unit));
    }
    if (field.options === "materialClasses") return classNames.map((c) => ({ value: c, label: c }));
    return [];
  };

  const materialPanel = useListPanel({
    list: materials,
    config: MATERIAL_PAGE,
    workspace,
    search,
    options,
    workbook: {
      build: (recs, { rate }) => buildMaterialsWorkbook(recs, { rate, materialClasses: classNames }),
      read: (wb, { rate }) => readMaterialsWorkbook(wb, { rate }),
    },
  });

  const formulaPanel = useListPanel({
    list: formulas,
    config: FORMULA_PAGE,
    workspace,
    search,
    options,
    extraColumns: [
      {
        key: "check",
        label: "Check",
        render: (it) => {
          const c = validateFormula(it.rec.Formula);
          return c.valid ? (
            <span className="text-success">Valid ({c.desktopVariables.join(", ") || "no variables"})</span>
          ) : (
            <span className="text-danger">{c.error}</span>
          );
        },
      },
    ],
  });

  const mapPanel = useListPanel({
    list: maps,
    config: FORMULA_MAP_PAGE,
    workspace,
    search,
    options,
    optionLabel: (field, v) => {
      if (field.key !== "FormulaConfig") return v === undefined || v === null || v === "" ? "--" : String(v);
      const cfg = parseFormulaConfig(v);
      if (!cfg) return "--";
      const mapped = (cfg.VariableParamMap || [])
        .map((m) => {
          const p = MATERIAL_FORMULA_PARAMETERS.find((x) => x.value === m.Parameter);
          return `${m.Variable} = ${p ? p.label : "unmapped"}`;
        })
        .join("; ");
      return `${cfg.FormulaName || "(unnamed)"}: ${cfg.Formula}${mapped ? ` [${mapped}]` : ""}`;
    },
    onOpenEditor: (m) => setMapModal(m),
  });

  const panel = { materials: materialPanel, formulas: formulaPanel, maps: mapPanel }[tab];

  return (
    <DatabasePageShell
      title="Materials & Formulas"
      workspace={workspace}
      lists={[materials, formulas, maps]}
      searchValue={search}
      setSearchValue={setSearch}
      onAdd={panel.onAdd}
      onEdit={panel.onEdit}
      onDelete={panel.onDelete}
      onClone={panel.onClone}
      onExport={panel.onExport}
      onImport={panel.onImport}
      beforeSave={() => {
        const bad = formulas.items.find((it) => it.status !== "clean" && !validateFormula(it.rec.Formula).valid);
        return bad ? `Formula "${bad.rec.Code}" is not valid: ${validateFormula(bad.rec.Formula).error}` : null;
      }}
    >
      <ul className="nav nav-tabs mb-3">
        {TABS.map((t) => (
          <li className="nav-item" key={t.key}>
            <button
              type="button"
              className={`nav-link btn btn-link ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          </li>
        ))}
      </ul>
      {tab === "formulas" && (
        <p className="f-12 text-muted">
          Formulas are checked with the same parser the estimating app uses. Variables are mapped to model parameters
          per catalog and material on the Formula assignments tab.
        </p>
      )}
      {panel.view}
      <FormulaConfigModal
        open={!!mapModal}
        initial={mapModal ? mapModal.initial : null}
        formulas={formulaChoices(formulas.rawRows).filter((f) => f.Formula)}
        onCancel={() => setMapModal(null)}
        onSubmit={(rec) => {
          if (mapModal.mode === "edit") maps.edit(mapModal.key, rec);
          else maps.add(rec);
          setMapModal(null);
        }}
      />
    </DatabasePageShell>
  );
}

